import { useState } from "react";

export default function ErrorTest() {
  const [count, setCount] = useState(0);

  const triggerRenderError = () => {
    // Force une erreur de rendu React
    throw new Error("Test: Erreur de rendu React intentionnelle");
  };

  const triggerAsyncError = async () => {
    // Erreur dans une promise
    await new Promise((resolve) => setTimeout(resolve, 100));
    throw new Error("Test: Erreur asynchrone intentionnelle");
  };

  const triggerConsoleError = () => {
    // Erreur dans la console
    console.error("Test: Console error intentionnelle");
    throw new Error("Test: Erreur console intentionnelle");
  };

  const triggerUndefinedError = () => {
    // Erreur undefined/null
    const obj: any = null;
    console.log(obj.property); // TypeError
  };

  const triggerNetworkError = async () => {
    // Simuler une erreur réseau
    try {
      await fetch('https://invalid-domain-that-does-not-exist-123456789.com');
    } catch (error) {
      throw new Error("Test: Erreur réseau intentionnelle");
    }
  };

  return (
    <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-p-4">
      <div className="tw-max-w-2xl tw-w-full tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-8">
        <div className="tw-mb-6">
          <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-2">
            🧪 Page de Test des Erreurs
          </h1>
          <p className="tw-text-gray-600">
            Utilisez cette page pour tester la capture d'erreurs dans Sentry.
          </p>
          <p className="tw-text-sm tw-text-amber-600 tw-mt-2">
            ⚠️ Cette page est réservée aux tests en environnement de production.
          </p>
        </div>

        <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4 tw-mb-6">
          <p className="tw-text-sm tw-text-blue-800">
            <strong>Note:</strong> Chaque bouton déclenche un type d'erreur différent.
            Vérifiez que l'erreur apparaît bien dans Sentry avec les bons tags.
          </p>
        </div>

        <div className="tw-space-y-4">
          {/* Compteur de test */}
          <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-border tw-border-gray-200">
            <p className="tw-text-sm tw-text-gray-600 tw-mb-2">État React (pour vérifier que le composant fonctionne) :</p>
            <div className="tw-flex tw-items-center tw-gap-3">
              <button
                onClick={() => setCount(count + 1)}
                className="tw-px-4 tw-py-2 tw-bg-green-600 tw-text-white tw-rounded-md hover:tw-bg-green-700 tw-text-sm"
              >
                Incrémenter: {count}
              </button>
              <span className="tw-text-gray-500 tw-text-sm">(devrait fonctionner normalement)</span>
            </div>
          </div>

          {/* Erreur de rendu React */}
          <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
            <h3 className="tw-font-semibold tw-text-gray-900 tw-mb-2">
              1. Erreur de Rendu React
            </h3>
            <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
              Déclenche une erreur dans le cycle de rendu. Devrait être capturée par ErrorBoundary.
            </p>
            {count >= 100 && triggerRenderError()}
            <button
              onClick={() => setCount(100)}
              className="tw-w-full tw-px-4 tw-py-3 tw-bg-red-600 tw-text-white tw-font-medium tw-rounded-md hover:tw-bg-red-700 tw-transition-colors"
            >
              Déclencher erreur React
            </button>
          </div>

          {/* Erreur asynchrone */}
          <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
            <h3 className="tw-font-semibold tw-text-gray-900 tw-mb-2">
              2. Erreur Asynchrone
            </h3>
            <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
              Erreur dans une Promise. Devrait être capturée automatiquement par Sentry.
            </p>
            <button
              onClick={() => triggerAsyncError()}
              className="tw-w-full tw-px-4 tw-py-3 tw-bg-orange-600 tw-text-white tw-font-medium tw-rounded-md hover:tw-bg-orange-700 tw-transition-colors"
            >
              Déclencher erreur async
            </button>
          </div>

          {/* Erreur console */}
          <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
            <h3 className="tw-font-semibold tw-text-gray-900 tw-mb-2">
              3. Erreur Console
            </h3>
            <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
              Erreur JavaScript classique dans un handler. Devrait être capturée par Sentry.
            </p>
            <button
              onClick={() => triggerConsoleError()}
              className="tw-w-full tw-px-4 tw-py-3 tw-bg-purple-600 tw-text-white tw-font-medium tw-rounded-md hover:tw-bg-purple-700 tw-transition-colors"
            >
              Déclencher erreur console
            </button>
          </div>

          {/* Erreur undefined/null */}
          <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
            <h3 className="tw-font-semibold tw-text-gray-900 tw-mb-2">
              4. TypeError (undefined)
            </h3>
            <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
              Accès à une propriété sur null. Erreur TypeError classique.
            </p>
            <button
              onClick={() => triggerUndefinedError()}
              className="tw-w-full tw-px-4 tw-py-3 tw-bg-pink-600 tw-text-white tw-font-medium tw-rounded-md hover:tw-bg-pink-700 tw-transition-colors"
            >
              Déclencher TypeError
            </button>
          </div>

          {/* Erreur réseau */}
          <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
            <h3 className="tw-font-semibold tw-text-gray-900 tw-mb-2">
              5. Erreur Réseau
            </h3>
            <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
              Fetch vers un domaine invalide. Devrait générer une erreur réseau.
            </p>
            <button
              onClick={() => triggerNetworkError()}
              className="tw-w-full tw-px-4 tw-py-3 tw-bg-indigo-600 tw-text-white tw-font-medium tw-rounded-md hover:tw-bg-indigo-700 tw-transition-colors"
            >
              Déclencher erreur réseau
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="tw-mt-8 tw-pt-6 tw-border-t tw-border-gray-200">
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="tw-w-full tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-md hover:tw-bg-gray-300 tw-transition-colors"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
